import type { ClientLocationPlan } from "api-types/locationPlans/locationPlansResponses";
import type { ClientMasterPlan, ClientMasterPlanReportingUnit } from "api-types/masterPlans/masterPlansResponses";
import type { FilterPair } from "common/utils/search";
import {
  MasterPlanFilter,
  MasterPlanStatusValue,
  MasterPlanTypeValue,
} from "lib/components/builders/communities/plans/masterPlanFiltersConstants";

export function sortReportingUnitsBySortIndex(
  reportingUnits: ClientMasterPlanReportingUnit[],
): ClientMasterPlanReportingUnit[] {
  return [...reportingUnits].sort((a, b) => a.sortIndex - b.sortIndex);
}

/** Sorted reporting units when the master plan is already available. */
export function getSortedReportingUnits(
  masterPlan: ClientMasterPlan | null | undefined,
): ClientMasterPlanReportingUnit[] {
  return sortReportingUnitsBySortIndex(masterPlan?.reportingUnits ?? []);
}

/** A location plan defers to its master plan for reporting units; a master plan is its own source. */
function findMasterPlan(
  plan: ClientMasterPlan | ClientLocationPlan,
  masterPlans: ClientMasterPlan[],
): ClientMasterPlan | undefined {
  const masterPlanId = "masterPlanId" in plan && plan.masterPlanId ? plan.masterPlanId : plan.id;
  return masterPlans.find((mp) => mp.id === masterPlanId);
}

/** Resolves sorted reporting units for a plan by looking up its master plan. */
export function getMasterPlanReportingUnits(
  plan: ClientMasterPlan | ClientLocationPlan,
  masterPlans: ClientMasterPlan[],
): ClientMasterPlanReportingUnit[] {
  return sortReportingUnitsBySortIndex(findMasterPlan(plan, masterPlans)?.reportingUnits ?? []);
}

/** Unit count for a plan, without the sort `getMasterPlanReportingUnits` does for display. */
function getPlanUnitCount(plan: ClientMasterPlan | ClientLocationPlan, masterPlans: ClientMasterPlan[]): number {
  return findMasterPlan(plan, masterPlans)?.reportingUnits?.length ?? 0;
}

/** Plan ID → unit count, for showing unit counts and comparing them against available lots. */
export function getUnitCountsByPlanId(
  plans: (ClientMasterPlan | ClientLocationPlan)[],
  masterPlans: ClientMasterPlan[],
): Record<string, number> {
  return Object.fromEntries(plans.map((plan) => [plan.id, getPlanUnitCount(plan, masterPlans)]));
}

/** True when the given master plan already has reporting units. Prefer when the master is in hand. */
export function isMasterPlanMultiUnit(masterPlan: ClientMasterPlan | undefined): boolean {
  return (masterPlan?.reportingUnits?.length ?? 0) > 0;
}

/**
 * True when a master or location plan is multi-unit, resolving the master from `masterPlans` when needed.
 * Use `isMasterPlanMultiUnit` instead when you already have the master plan object.
 */
export function isMultiUnitPlan(plan: ClientMasterPlan | ClientLocationPlan, masterPlans: ClientMasterPlan[]): boolean {
  return getPlanUnitCount(plan, masterPlans) > 0;
}

/** Display label for a master plan's type, used by the Plan Library's plan-type column. */
export function getMasterPlanTypeLabel(masterPlan: ClientMasterPlan): string {
  return isMasterPlanMultiUnit(masterPlan) ? "Multi-unit" : "Single family";
}

/**
 * Resolves the value a master plan contributes to each Plan Library filter, for
 * `filterListBySearchAndFilters`. Returns undefined for unknown filter names so the
 * plan simply fails to match rather than throwing.
 */
export function getMasterPlanFilterValue(masterPlan: ClientMasterPlan, filterName: string): string | undefined {
  if (filterName === MasterPlanFilter.STATUS) {
    return masterPlan.archivedAt ? MasterPlanStatusValue.ARCHIVED : MasterPlanStatusValue.ACTIVE;
  }
  if (filterName === MasterPlanFilter.PLAN_TYPE) {
    return isMasterPlanMultiUnit(masterPlan) ? MasterPlanTypeValue.MULTI_UNIT : MasterPlanTypeValue.SINGLE_FAMILY;
  }
  return undefined;
}

/** The values currently selected for one filter, or an empty array when the filter is unset. */
export function getSelectedFilterValues(filters: FilterPair[], filterName: MasterPlanFilter): string[] {
  return filters.find(([name]) => name === filterName)?.[1] ?? [];
}
